import * as React from "react";
import { Tabs, Accordion } from "@/components/ui";
import { useMemo, useState } from "react";
import { CalendarIcon, Globe, Megaphone, Share2, Users } from "lucide-react";
import { ClicksByDayChart } from "./ClicksByDayChart";
import { ClicksByNetworkChart } from "@/containers/Coach/SmartLink/StatsDialog/components/ClicksByNetworkChart";
import { HorizontalBarChart } from "@/containers/Coach/SmartLink/StatsDialog/components/HorizontalBarChart";
import { SmartLinkStats } from "@/services/bedrock";

interface ReportTabsProps {
  stats: SmartLinkStats;
}

export function ReportTabs({ stats }: ReportTabsProps) {
  const [activeTab, setActiveTab] = useState("performance");
  const showSourceTab = stats.clicksByCampaign || stats.clicksByPrompt || stats.clicksBySmartLink;

  const sourceCharts = useMemo(() => {
    const charts = [];
    if (stats.clicksByCampaign) {
      charts.push({
        key: "campaigns-chart",
        title: "Clicks by Campaign",
        data: stats.clicksByCampaign,
        index: "campaignName",
      });
    }
    if (stats.clicksByPrompt) {
      charts.push({
        key: "prompts-chart",
        title: "Clicks by Prompt",
        data: stats.clicksByPrompt,
        index: "promptTitle",
      });
    }
    if (stats.clicksBySmartLink) {
      charts.push({
        key: "smart-links-chart",
        title: "Clicks by Smart Link",
        data: stats.clicksBySmartLink,
        index: "smartLinkName",
      });
    }

    return charts;
  }, [stats.clicksByCampaign, stats.clicksByPrompt, stats.clicksBySmartLink]);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <Tabs.List className="grid grid-cols-5 w-full bg-slate-100 p-1 rounded-xl">
        {stats.clicksByDay && (
          <Tabs.Trigger value="performance" className="flex items-center gap-2">
            <CalendarIcon className="size-4" />
            Time
          </Tabs.Trigger>
        )}
        {stats.clicksByNetwork && (
          <Tabs.Trigger value="networks" className="flex items-center gap-2">
            <Share2 className="size-4" />
            Networks
          </Tabs.Trigger>
        )}
        {stats.clicksByPlayer && (
          <Tabs.Trigger value="players" className="flex items-center gap-2">
            <Users className="size-4" />
            Players
          </Tabs.Trigger>
        )}
        {stats.clicksByRegion && (
          <Tabs.Trigger value="regions" className="flex items-center gap-2">
            <Globe className="size-4" />
            Regions
          </Tabs.Trigger>
        )}
        {showSourceTab && (
          <Tabs.Trigger value="source" className="flex items-center gap-2">
            <Megaphone className="size-4" />
            Source
          </Tabs.Trigger>
        )}
      </Tabs.List>

      <div className="p-4 border border-slate-300 rounded-2xl bg-white shadow-sm min-h-[200px]">
        {stats.clicksByDay && (
          <Tabs.Content value="performance" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Click Performance Over Time</h3>
            </div>
            <ClicksByDayChart data={stats.clicksByDay} />
          </Tabs.Content>
        )}
        {stats.clicksByNetwork && (
          <Tabs.Content value="networks" className="space-y-4">
            <h3 className="font-bold text-lg">Clicks by Social Network</h3>
            <ClicksByNetworkChart data={stats.clicksByNetwork} />
          </Tabs.Content>
        )}
        {stats.clicksByPlayer && (
          <Tabs.Content value="players" className="space-y-4">
            <h3 className="font-bold text-lg">Top Performing Players</h3>
            <HorizontalBarChart data={stats.clicksByPlayer} indexBy="playerName" />
          </Tabs.Content>
        )}
        {stats.clicksByRegion && (
          <Tabs.Content value="regions" className="space-y-4">
            <h3 className="font-bold text-lg">Top Clicks by Region</h3>
            <HorizontalBarChart data={stats.clicksByRegion} indexBy="state" />
          </Tabs.Content>
        )}
        {sourceCharts.length > 0 && (
          <Tabs.Content value="source" className="space-y-6 flex flex-col gap-4">
            <Accordion multiple defaultValue={sourceCharts.map(c => c.key)} className="w-full">
              {sourceCharts.map(chart => (
                <Accordion.Item key={chart.key} value={chart.key}>
                  <Accordion.Trigger>
                    <h3 className="font-bold text-lg">{chart.title}</h3>
                  </Accordion.Trigger>
                  <Accordion.Content>
                    <HorizontalBarChart data={chart.data} indexBy={chart.index} />
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion>
          </Tabs.Content>
        )}
      </div>
    </Tabs>
  );
}
